import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Package, Check, ShoppingBag, Trash2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useHouseholdRealtime } from '../hooks/useHouseholdRealtime'
import { calculateItemStatus, getExpirationDisplay, type ItemStatus } from '../lib/patternEngine'
import { usePreferences } from '../hooks/usePreferences'
import SummaryBar from '../components/pantry/SummaryBar'
import { LocationGroup } from '../components/home/LocationGroup'
import AddItemModal from '../components/pantry/AddItemModal'
import ItemDetailSheet from '../components/pantry/ItemDetailSheet'
import MarkAsOutSheet from '../components/pantry/MarkAsOutSheet'
import ConfirmExpirySheet, { type ExpiryItem } from '../components/pantry/ConfirmExpirySheet'
import { checkExpiringItems } from '../lib/notifications'
import { calculateRestockSummary, isShoppingEve } from '../lib/restockCalculator'
import { checkAndSendRestockNotification } from '../lib/restockNotification'
import { getShopUrgency, getEffectivePredictedShop } from '../lib/scheduleAnalyzer'
import type { ScheduleType } from '../lib/scheduleAnalyzer'
import RestockBanner from '../components/pantry/RestockBanner'
import { OptimizerTip, findTip } from '../components/pantry/OptimizerTip'
import { LOCATIONS } from '../constants/locations'
import { KitchenScanner } from '../components/pantry/KitchenScanner'
import RecipeScanner from '../components/pantry/RecipeScanner'
import TipCard from '../components/shared/TipCard'
import { useTip } from '../hooks/useTip'
import PantrySetupFlow from '../components/pantry/PantrySetupFlow'
import PantryAuditCard from '../components/pantry/PantryAuditCard'
import BulkBarcodeScanner from '../components/pantry/BulkBarcodeScanner'
import { HomeMasthead } from '../components/home/HomeMasthead'
import { HomeHero } from '../components/home/HomeHero'
import { HomeSearchBar } from '../components/home/HomeSearchBar'
import { HomeActionTiles } from '../components/home/HomeActionTiles'
import { PendingUnpackBanner } from '../components/home/PendingUnpackBanner'
import { ScanReceiptButton } from '../components/home/ScanReceiptButton'
import { ExpiringNudge } from '../components/home/ExpiringNudge'
import {
  useBulkDeletePantryItems,
  useRestorePantryItems,
} from '../hooks/useDeletePantryItems'
import { useAdjustQuantity, type AdjustReason } from '../hooks/useAdjustQuantity'
import { QuickQuantitySheet } from '../components/home/QuickQuantitySheet'
import { OutOfStockSheet } from '../components/home/OutOfStockSheet'
import { useAddItemToActiveList } from '../hooks/useAddItemToActiveList'


// ─── Types ────────────────────────────────────────────────────────────────────

interface PantryItem {
  id:                      string
  name:                    string
  emoji:                   string
  display_category:        string
  household_id:            string
  last_confirmed_at:       string | null
  avg_restock_cycle_days:  number | null
  
  confirmation_count:      number
  current_expiration_date: string | null
  track_expiration:        boolean
  status_override:         string | null
  location:                string | null
  current_quantity:        number | null
  quantity_updated_at:     string | null
  estimated_daily_use:     number | null
  quantity_unit:           string | null
  notes:                   string | null
}

type EnrichedItem = PantryItem & {
  status:        ItemStatus
  depletion:     number
  daysRemaining: number
  source:        'pattern' | 'expiration'
  current_expiration_date: string | null
}

// ─── Status sort order ────────────────────────────────────────────────────────

const STATUS_ORDER: Record<ItemStatus, number> = { red: 0, amber: 1, used: 2, green: 3, learning: 4 }

// ─── Effective status — resolves conflict between pattern and expiry ───────────

function getEffectiveStatus(
  patternStatus:        ItemStatus,
  patternDaysRemaining: number,
  expirationDate:       string | null,
): {
  status:        ItemStatus
  daysRemaining: number
  source:        'pattern' | 'expiration'
} {
  // Used items are intentionally finished — expiry is irrelevant
  if (patternStatus === 'used' || !expirationDate) {
    return { status: patternStatus, daysRemaining: patternDaysRemaining, source: 'pattern' }
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const expDate = new Date(expirationDate + 'T00:00:00')
  expDate.setHours(0, 0, 0, 0)
  const daysUntilExpiry = Math.floor(
    (expDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  )

  // Already expired — always red
  if (daysUntilExpiry < 0) {
    return { status: 'red', daysRemaining: daysUntilExpiry, source: 'expiration' }
  }

  // Expires today or tomorrow — red
  if (daysUntilExpiry <= 1) {
    return { status: 'red', daysRemaining: daysUntilExpiry, source: 'expiration' }
  }

  // Expires in 2–3 days — at least amber
  if (daysUntilExpiry <= 3) {
    if (patternStatus === 'green' || patternStatus === 'learning') {
      return { status: 'amber', daysRemaining: daysUntilExpiry, source: 'expiration' }
    }
    return { status: patternStatus, daysRemaining: daysUntilExpiry, source: 'expiration' }
  }

  // Expiration is sooner than the pattern cycle — use the tighter constraint
  if (daysUntilExpiry < patternDaysRemaining) {
    const expirationStatus: ItemStatus = daysUntilExpiry <= 3 ? 'amber' : 'green'
    const statusPriority: Record<ItemStatus, number> = { red: 0, amber: 1, used: 2, green: 3, learning: 4 }
    const moreUrgent: ItemStatus =
      statusPriority[patternStatus] <= statusPriority[expirationStatus]
        ? patternStatus
        : expirationStatus

    return {
      status:        moreUrgent,
      daysRemaining: Math.min(daysUntilExpiry, patternDaysRemaining),
      source:        'expiration',
    }
  }

  // Pattern cycle is sooner — keep pattern as-is
  return { status: patternStatus, daysRemaining: patternDaysRemaining, source: 'pattern' }
}

// ─── Skeleton row ─────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 px-4 py-4 border-b border-[#F0EDE8]" style={{ minHeight: 64 }}>
      <div className="w-3 h-3 rounded-full bg-[#E8E2DC] animate-pulse shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-3 w-2/5 rounded bg-[#E8E2DC] animate-pulse" />
        <div className="h-2.5 w-1/3 rounded bg-[#E8E2DC] animate-pulse" />
      </div>
    </div>
  )
}

// ─── QuickActionRow — paper row with semantic tint, used by the quick-action sheet ──

function QuickActionRow({
  icon, tint, title, subtitle, onClick, isLast,
}: {
  icon:      React.ReactNode
  tint:      'warn' | 'ok' | 'forest' | 'critical'
  title:     string
  subtitle:  string
  onClick:   () => void
  isLast?:   boolean
}) {
  const tintMap = {
    warn:     { bg: 'bg-cream-warm',  fg: 'text-terracotta' },
    ok:       { bg: 'bg-ok-bg',       fg: 'text-forest'     },
    forest:   { bg: 'bg-ok-bg',       fg: 'text-forest'     },
    critical: { bg: 'bg-critical-bg', fg: 'text-critical'   },
  }[tint]

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition ${
        !isLast ? 'border-b border-border-hairline' : ''
      }`}
    >
      <div className={`h-9 w-9 rounded-[10px] flex items-center justify-center ${tintMap.bg} ${tintMap.fg}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-serif text-[15px] font-medium ${tintMap.fg}`}>{title}</div>
        <div className="text-[11px] italic font-serif text-ink-muted mt-0.5">{subtitle}</div>
      </div>
    </button>
  )
}

// ─── Toast ────────────────────────────────────────────────────────────────────

function Toast({ message, onUndo }: { message: string; onUndo?: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2 }}
      className="fixed top-5 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3
                 px-5 py-3 rounded-2xl bg-[#2C2A26] text-white text-sm font-medium shadow-xl"
    >
      <span className="whitespace-nowrap">{message}</span>
      {onUndo && (
        <button
          onClick={onUndo}
          className="text-[#A8D8B0] text-xs font-bold whitespace-nowrap
                     border-l border-white/20 pl-3 ml-1 hover:text-white transition-colors"
        >
          Undo
        </button>
      )}
    </motion.div>
  )
}

// ─── Home ─────────────────────────────────────────────────────────────────────

export default function Home() {
  const navigate     = useNavigate()
  const queryClient  = useQueryClient()
  const { user }     = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const { homeLayout, favoriteItemIds, summaryBar, isPremium, autoAddToListOnZero } = usePreferences()
  const tipHome = useTip('home-intro')

  useEffect(() => { document.title = 'Pantry — Your household memory' }, [])

  // ── Onboarding guard — safety net in case ProtectedRoute passes through ───

  const { data: userMeta, isLoading: userMetaLoading } = useQuery({
    queryKey: ['user-onboarding', user?.id],
    enabled:  !!user?.id,
    staleTime: 60_000,
    queryFn:  async () => {
      const { data } = await supabase
        .from('users')
        .select('onboarding_completed, household_id')
        .eq('id', user!.id)
        .maybeSingle()
      return data
    },
  })

  useEffect(() => {
    if (!userMetaLoading && userMeta && !userMeta.onboarding_completed) {
      navigate('/onboarding', { replace: true })
    }
  }, [userMeta, userMetaLoading, navigate])

  // ── Upgrade success — stripe redirects back with ?upgraded=true ───────────

  useEffect(() => {
    if (searchParams.get('upgraded') === 'true') {
      showToast('Welcome to Pantry +! 🎉')
      queryClient.invalidateQueries({ queryKey: ['userMeta'] })
      const next = new URLSearchParams(searchParams)
      next.delete('upgraded')
      setSearchParams(next, { replace: true })
    }
  // showToast is defined below — stable ref via closure, no dep needed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Modal / sheet state ───────────────────────────────────────────────────

  const [dismissedRestock,    setDismissedRestock]    = useState(false)
  const [quickActionItem,     setQuickActionItem]     = useState<EnrichedItem | null>(null)
  const [tipDismissed,        setTipDismissed]        = useState(
    () => localStorage.getItem('tip-dismissed') === new Date().toDateString()
  )
  const [searchQuery,          setSearchQuery]          = useState('')
  const [showAddModal,         setShowAddModal]         = useState(false)
  const [showBulkScanner,      setShowBulkScanner]      = useState(false)
  const [showSetupFlow,        setShowSetupFlow]        = useState(false)
  const [showKitchenScanner,   setShowKitchenScanner]   = useState(false)
  const [showRecipeScanner,    setShowRecipeScanner]    = useState(false)

  const [detailItem,       setDetailItem]       = useState<EnrichedItem | null>(null)
  const [editItem,         setEditItem]         = useState<EnrichedItem | null>(null)
  const [markAsOutItem,    setMarkAsOutItem]    = useState<EnrichedItem | null>(null)
  const [confirmExpiryItem, setConfirmExpiryItem] = useState<ExpiryItem | null>(null)

  // Force-show content after 3 s so a stale query never blocks the whole page
  const [forceShow, setForceShow] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setForceShow(true), 3000)
    return () => clearTimeout(t)
  }, [])

  // Toast state — supports optional Undo button
  const [toastData,    setToastData]    = useState<{ message: string; showUndo: boolean } | null>(null)
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Undo data lives in a ref so it doesn't trigger re-renders.
  // Tagged union so the handler can branch on what's being undone.
  type UndoData =
    | {
        kind:       'mark-as-out'
        itemId:     string
        prev: {
          last_confirmed_at:      string | null
          confirmation_count:     number
          avg_restock_cycle_days: number | null
        }
        listItemId: string | null
      }
    | {
        kind: 'bulk-delete'
        rows: any[]
      }
  const undoRef = useRef<UndoData | null>(null)

  function clearToast() {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    setToastData(null)
  }

  function showToast(message: string) {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    undoRef.current = null
    setToastData({ message, showUndo: false })
    toastTimerRef.current = setTimeout(clearToast, 3000)
  }

  function showUndoToast(message: string) {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    setToastData({ message, showUndo: true })
    toastTimerRef.current = setTimeout(() => {
      clearToast()
      undoRef.current = null
    }, 5000)
  }

  // ── Undo — reverts the last destructive action ────────────────────────────

  async function handleUndo() {
    const data = undoRef.current
    if (!data) return
    clearToast()
    undoRef.current = null

    if (data.kind === 'bulk-delete') {
      if (data.rows.length > 0) {
        await supabase.from('pantry_items').insert(data.rows)
      }
      queryClient.invalidateQueries({ queryKey: ['pantry-items', householdId] })
      queryClient.invalidateQueries({ queryKey: ['home',         householdId] })
      return
    }

    // mark-as-out branch
    await supabase
      .from('pantry_items')
      .update({
        last_confirmed_at:      data.prev.last_confirmed_at,
        confirmation_count:     data.prev.confirmation_count,
        avg_restock_cycle_days: data.prev.avg_restock_cycle_days,
      })
      .eq('id', data.itemId)

    if (data.listItemId) {
      await supabase.from('list_items').delete().eq('id', data.listItemId)
    }

    queryClient.invalidateQueries({ queryKey: ['pantry-items', householdId] })
    queryClient.invalidateQueries({ queryKey: ['listItems'] })
  }

  // ── Mark as out — called by MarkAsOutSheet onConfirm ─────────────────────

  async function handleMarkAsOutConfirm(lastBoughtAt: Date | null) {
    const item = markAsOutItem
    if (!item || !householdId) return
    setMarkAsOutItem(null)

    // Save values for undo
    undoRef.current = {
      kind:   'mark-as-out',
      itemId: item.id,
      prev: {
        last_confirmed_at:      item.last_confirmed_at,
        confirmation_count:     item.confirmation_count,
        avg_restock_cycle_days: item.avg_restock_cycle_days,
      },
      listItemId: null,
    }

    // Calculate new values
    let newLastConfirmed = item.last_confirmed_at
    let newCycle         = item.avg_restock_cycle_days

    if (lastBoughtAt !== null) {
      newLastConfirmed = lastBoughtAt.toISOString()
      const daysSince  = Math.max(1, Math.round(
        (Date.now() - lastBoughtAt.getTime()) / (1000 * 60 * 60 * 24)
      ))
      newCycle = item.avg_restock_cycle_days ?? daysSince
    }

    const newConfCount = Math.max(item.confirmation_count, 3)

    // Update pantry_items
    await supabase
      .from('pantry_items')
      .update({
        last_confirmed_at:      newLastConfirmed,
        avg_restock_cycle_days: newCycle,
        confirmation_count:     newConfCount,
      })
      .eq('id', item.id)

    // Check for an active (not completed) shopping list
    const { data: activeList } = await supabase
      .from('shopping_lists')
      .select('id')
      .eq('household_id', householdId)
      .is('completed_at', null)
      .order('generated_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (activeList) {
      // Only insert if not already on the list
      const { data: existing } = await supabase
        .from('list_items')
        .select('id')
        .eq('list_id', activeList.id)
        .eq('item_id', item.id)
        .neq('status', 'removed')
        .maybeSingle()

      if (!existing) {
        const { data: newRow } = await supabase
          .from('list_items')
          .insert({
            list_id:       activeList.id,
            item_id:       item.id,
            status:        'pending',
            urgency_score: 999,
            sort_order:    0,
            is_one_off:    false,
          })
          .select('id')
          .single()

        if (undoRef.current?.kind === 'mark-as-out') {
          undoRef.current.listItemId = newRow?.id ?? null
        }
      }

      showUndoToast(`${item.name} marked as out — added to list`)
    } else {
      showUndoToast(`${item.name} marked as out`)
    }

    // Refresh both pantry and list caches
    queryClient.invalidateQueries({ queryKey: ['pantry-items', householdId] })
    queryClient.invalidateQueries({ queryKey: ['latestList'] })
    queryClient.invalidateQueries({ queryKey: ['listItems'] })
  }

  function handleItemSaved(itemName: string, isEdit: boolean) {
    showToast(isEdit ? `${itemName} updated` : `${itemName} added to your pantry`)
  }

  function openAdd() {
    setEditItem(null)
    setShowAddModal(true)
  }

  const openDetail = useCallback((item: EnrichedItem) => {
    setDetailItem(item)
  }, [])

  function handleEditFromDetail() {
    if (!detailItem) return
    setEditItem(detailItem)
    setDetailItem(null)
    setShowAddModal(true)
  }

  function handleMarkAsOutFromDetail() {
    if (!detailItem) return
    const item = detailItem
    setDetailItem(null)
    setMarkAsOutItem(item)
  }

  function handleDeleted(itemName: string) {
    setDetailItem(null)
    showToast(`${itemName} removed`)
  }

  // ── Step 1: resolve household_id ──────────────────────────────────────────

  const { data: householdId } = useQuery({
    queryKey: ['householdId', user?.id],
    enabled:  !!user?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('users')
        .select('household_id')
        .eq('id', user!.id)
        .maybeSingle()
      if (error) {
        console.error('householdId query error:', error)
        return null
      }
      return (data?.household_id as string) ?? null
    },
  })

  // Realtime sync: pantry-affecting tables. Invalidate caches when any
  // household member mutates pantry items, batches, or quantity adjustments.
  useHouseholdRealtime(householdId, {
    tables: ['pantry_items', 'item_batches', 'quantity_adjustments', 'confirmations'],
    invalidateMap: {
      pantry_items:         [['pantry-items', householdId], ['home', householdId]],
      item_batches:         [['pantry-items', householdId]],
      quantity_adjustments: [['pantry-items', householdId]],
      confirmations:        [['confirmations', householdId]],
    },
  })

  // ── Confirmations (for restock cost estimates) ────────────────────────────

  const { data: confirmationsData } = useQuery({
    queryKey: ['confirmations', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('confirmations')
        .select('item_id, price_paid, confirmed_at, was_on_sale')
        .eq('household_id', householdId!)
        .not('price_paid', 'is', null)
        .order('confirmed_at', { ascending: false })
      return data ?? []
    },
  })

  // ── Household (for shopping day) ──────────────────────────────────────────

  const { data: householdData } = useQuery({
    queryKey: ['household', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('households')
        .select('shopping_day, size')
        .eq('id', householdId!)
        .single()
      return data
    },
  })

  // ── Household schedule (smart prediction) ────────────────────────────────

  const { data: householdSchedule } = useQuery({
    queryKey: ['household-schedule', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('households')
        .select(`
          schedule_type,
          avg_shop_interval_days,
          last_shop_date,
          predicted_next_shop,
          schedule_confidence,
          shop_history,
          schedule_override_days,
          schedule_override_until
        `)
        .eq('id', householdId!)
        .single()
      return data
    },
  })

  // Roll a stale manual-schedule prediction forward before deriving urgency,
  // so the home banner doesn't say "Shop -3 days ago" the morning after.
  const effectivePredicted = getEffectivePredictedShop(
    householdSchedule?.predicted_next_shop ?? null,
    (householdSchedule?.schedule_type ?? 'learning') as ScheduleType,
    householdSchedule?.schedule_override_days ?? null,
  )
  const shopUrgency = getShopUrgency(
    effectivePredicted,
    householdSchedule?.schedule_confidence ?? 0,
  )

  const householdShopInterval = householdSchedule?.avg_shop_interval_days ?? null

  // ── Active list (for auto-generated banner) ───────────────────────────────

  const { data: latestList } = useQuery({
    queryKey: ['latestListMeta', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('shopping_lists')
        .select('id, generated_at, completed_at')
        .eq('household_id', householdId!)
        .is('completed_at', null)
        .order('generated_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      return data
    },
  })

  const wasAutoGeneratedToday = !!latestList?.generated_at &&
    !latestList.completed_at &&
    new Date(latestList.generated_at).toDateString() === new Date().toDateString()

  const [listBannerDismissed, setListBannerDismissed] = useState(false)


  // ── Step 2: fetch active pantry items ─────────────────────────────────────

  const { data: rawItems, isLoading } = useQuery({
    queryKey:             ['pantry-items', householdId],
    enabled:              !!householdId,
    refetchOnWindowFocus: true,
    refetchInterval:      30_000,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('pantry_items')
        .select(
          'id, name, emoji, display_category, household_id, ' +
          'last_confirmed_at, avg_restock_cycle_days, confirmation_count, ' +
          'current_expiration_date, track_expiration, status_override, location, ' +
          'current_quantity, quantity_updated_at, estimated_daily_use, quantity_unit, notes'
        )
        .eq('household_id', householdId!)
        .eq('is_active', true)
      if (error) throw error
      return data as unknown as PantryItem[]
    },
  })

  // ── Enrich + sort ─────────────────────────────────────────────────────────

  const items: EnrichedItem[] = (rawItems ?? [])
    .map(item => {
      const { status: patternStatus, depletion, daysRemaining: patternDaysRemaining } =
        calculateItemStatus(
          item.last_confirmed_at ? new Date(item.last_confirmed_at) : null,
          item.avg_restock_cycle_days,
          item.confirmation_count,
          item.status_override,
          householdShopInterval,
        )
      const { status, daysRemaining, source } = getEffectiveStatus(
        patternStatus,
        patternDaysRemaining,
        item.track_expiration ? (item.current_expiration_date ?? null) : null,
      )
      return { ...item, status, depletion, daysRemaining, source }
    })
    .sort((a, b) => {
      const aFav = favoriteItemIds.includes(a.id)
      const bFav = favoriteItemIds.includes(b.id)
      if (aFav !== bFav) return aFav ? -1 : 1

      // Urgent expiring items bubble to the top regardless of stock status
      const aUrgent = (a.track_expiration && getExpirationDisplay(a.current_expiration_date)?.urgent) ? -1 : 0
      const bUrgent = (b.track_expiration && getExpirationDisplay(b.current_expiration_date)?.urgent) ? -1 : 0
      if (aUrgent !== bUrgent) return aUrgent - bUrgent

      return STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
    })

  // ── Restock summary ───────────────────────────────────────────────────────

  const shoppingDay     = householdData?.shopping_day ?? 6
  const isEve           = isShoppingEve(shoppingDay)

  const restockSummary  = useMemo(() => {
    if (!rawItems || !confirmationsData) return null
    return calculateRestockSummary(rawItems, confirmationsData)
  }, [rawItems, confirmationsData])

  const outCount     = items.filter(i => i.status === 'red').length
  const lowCount     = items.filter(i => i.status === 'amber').length
  const usedCount    = items.filter(i => i.status === 'used').length
  const stockedCount = items.filter(i => i.status === 'green').length

  // Items expiring within the next 7 days (or already expired within last 2 days)
  const today7 = new Date(); today7.setDate(today7.getDate() + 7)
  const past2  = new Date(); past2.setDate(past2.getDate() - 2)
  const expiringItems = items.filter(i => {
    if (!i.track_expiration || !i.current_expiration_date) return false
    const d = new Date(i.current_expiration_date + 'T00:00:00')
    return d >= past2 && d <= today7
  })

  // Fire browser notifications once per page load when items are ready.
  // Any failure here (permission revoked, mobile SW quirk, etc.) must stay
  // silent — it's a background nicety, not something that can crash Home.
  useEffect(() => {
    if (!rawItems?.length) return
    checkExpiringItems(rawItems).catch(err => {
      console.warn('Expiry notification check failed:', err)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!rawItems])

  // Refresh expiration countdowns every hour so "Expires today" stays accurate
  useEffect(() => {
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['pantry-items', householdId] })
    }, 1000 * 60 * 60)
    return () => clearInterval(interval)
  }, [householdId])

  // Auto-launch setup flow on first visit for empty pantries
  useEffect(() => {
    if (!isLoading && items.length === 0 && householdId) {
      const seen = localStorage.getItem('pantry-setup-seen')
      if (!seen) setShowSetupFlow(true)
    }
  }, [isLoading, items.length, householdId])

  // ── Realtime — reflect other household members' changes instantly ──────────
  useEffect(() => {
    if (!householdId) return
    const channel = supabase
      .channel('pantry-changes-' + householdId)
      .on(
        'postgres_changes',
        {
          event:  '*',
          schema: 'public',
          table:  'pantry_items',
          filter: 'household_id=eq.' + householdId,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['pantry-items', householdId] })
        }
      )
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [householdId])

  // Reset dismissed banner at midnight
  useEffect(() => {
    const now               = new Date()
    const midnight          = new Date(now)
    midnight.setHours(24, 0, 0, 0)
    const msUntilMidnight   = midnight.getTime() - now.getTime()
    const timer             = setTimeout(() => setDismissedRestock(false), msUntilMidnight)
    return () => clearTimeout(timer)
  }, [])

  // Send shopping-eve browser notification (premium only, once per day)
  useEffect(() => {
    if (!isPremium || !restockSummary || !isEve) return
    checkAndSendRestockNotification(
      shoppingDay,
      restockSummary.itemCount,
      restockSummary.estimatedTotal,
      true,
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restockSummary, isEve, isPremium])

  // Auto-prompt for expired items not yet confirmed (once per session per item)
  useEffect(() => {
    if (!expiringItems.length || confirmExpiryItem) return
    const todayStr = new Date().toISOString().split('T')[0]
    const candidate = expiringItems.find(i => {
      if (!i.current_expiration_date) return false
      if (i.current_expiration_date > todayStr) return false // not yet expired
      const key = `expiryAsked-${i.id}`
      if (sessionStorage.getItem(key)) return false
      return true
    })
    if (candidate) {
      sessionStorage.setItem(`expiryAsked-${candidate.id}`, '1')
      setConfirmExpiryItem({
        id:                      candidate.id,
        name:                    candidate.name,
        emoji:                   candidate.emoji,
        household_id:            candidate.household_id,
        current_expiration_date: candidate.current_expiration_date,
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiringItems.length])

  // ── Group items by location ────────────────────────────────────────────────

  const groupedByLocation = useMemo(() => {
    const t7 = new Date(); t7.setDate(t7.getDate() + 7)
    const p2 = new Date(); p2.setDate(p2.getDate() - 2)
    return Object.values(LOCATIONS).map(loc => {
      const locItems    = items.filter(i => (i.location ?? 'pantry') === loc.id)
      const urgentItems = locItems.filter(i => i.status === 'red' || i.status === 'used')
      const expiringCount = locItems.filter(i => {
        if (!i.track_expiration || !i.current_expiration_date) return false
        const d = new Date(i.current_expiration_date + 'T00:00:00')
        return d >= p2 && d <= t7
      }).length
      return { ...loc, items: locItems, urgentItems, urgentCount: urgentItems.length, expiringCount }
    }).filter(g => g.items.length > 0)
  }, [items])

  const searchResults = searchQuery.trim()
    ? items.filter(i =>
        i.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        i.display_category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : []

  // ── Select mode (mass delete) ────────────────────────────────────────────

  const bulkDelete = useBulkDeletePantryItems()
  const restore    = useRestorePantryItems()

  const [selectMode,  setSelectMode]  = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const allItemIds   = useMemo(() => items.map(i => i.id), [items])
  const totalVisible = allItemIds.length
  const totalSelected = selectedIds.size
  const allSelected   = totalSelected > 0 && totalSelected === totalVisible

  function exitSelectMode() {
    setSelectMode(false)
    setSelectedIds(new Set())
  }

  function toggleSelectItem(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function selectAllItems() {
    setSelectedIds(new Set(allItemIds))
  }

  function clearSelection() {
    setSelectedIds(new Set())
  }

  function selectAllInLocation(locationId: string) {
    const ids = items
      .filter(i => (i.location ?? 'pantry') === locationId)
      .map(i => i.id)
    setSelectedIds(prev => {
      const next = new Set(prev)
      const already = ids.length > 0 && ids.every(id => next.has(id))
      if (already) ids.forEach(id => next.delete(id))
      else         ids.forEach(id => next.add(id))
      return next
    })
  }

  async function handleBulkDelete() {
    if (selectedIds.size === 0 || !householdId) return
    const ids = Array.from(selectedIds)

    try {
      const result = await bulkDelete.mutateAsync({ householdId, itemIds: ids })
      const snapshot = result.deleted

      // Stash snapshot for handleUndo and surface the existing 5s undo toast.
      undoRef.current = { kind: 'bulk-delete', rows: snapshot }
      exitSelectMode()
      showUndoToast(`Deleted ${ids.length} item${ids.length === 1 ? '' : 's'}`)
    } catch {
      showToast('Could not delete. Try again.')
    }
  }

  // restore is wired through handleUndo via undoRef — keep this reference so
  // tree-shaking doesn't drop the hook even if a future refactor inlines it.
  void restore

  // ── Quick quantity adjust sheet + out-of-stock follow-up ─────────────────

  const adjustQty = useAdjustQuantity()
  const addToList = useAddItemToActiveList()
  const [qtyAdjustItem, setQtyAdjustItem] = useState<any | null>(null)
  const [oosItem,       setOosItem]       = useState<any | null>(null)
  const [oosHasActive,  setOosHasActive]  = useState(false)

  // The auto-add-on-zero pref lives in the JSON preferences (see usePreferences);
  // defaults to true when the field is missing on legacy preference rows.
  const autoAddOnZero = autoAddToListOnZero ?? true

  async function handleConfirmQtyAdjust(newTotal: number, reason: AdjustReason) {
    if (!qtyAdjustItem || !householdId) return
    const oldTotal = qtyAdjustItem.current_quantity ?? 0
    const delta    = newTotal - oldTotal
    if (delta === 0) return

    const itemSnapshot = qtyAdjustItem  // capture before we close the sheet

    try {
      await adjustQty.mutateAsync({
        itemId:      itemSnapshot.id,
        householdId,
        oldTotal,
        newTotal,
        delta,
        reason,
      })
      const verb = delta > 0 ? 'Added' : 'Used'
      showToast(`${verb} ${Math.abs(delta)} · ${itemSnapshot.name}`)
    } catch (err) {
      console.error('[Home] qty adjust failed:', err)
      showToast('Could not adjust quantity. Try again.')
      return
    }

    // Out-of-stock prompt: only when qty just transitioned to 0, the user
    // hasn't opted out, and the reason isn't 'expired' (waste flow handles
    // those separately and we don't want stacked sheets).
    if (newTotal === 0 && oldTotal > 0 && autoAddOnZero && reason !== 'expired') {
      setQtyAdjustItem(null)  // close the qty sheet first

      const { data: activeLists } = await supabase
        .from('shopping_lists')
        .select('id')
        .eq('household_id', householdId)
        .is('completed_at', null)
        .is('unpacked_at',  null)
        .limit(1)

      setOosHasActive(!!activeLists?.length)
      setOosItem(itemSnapshot)
    }
  }

  async function handleOosConfirm() {
    if (!oosItem || !householdId) return
    try {
      const result = await addToList.mutateAsync({
        itemId:       oosItem.id,
        itemName:     oosItem.name,
        householdId,
        startNewList: !oosHasActive,
      })
      const name = oosItem.name
      setOosItem(null)
      if (result.alreadyOnList)      showToast(`${name} was already on your list.`)
      else if (result.startedNew)    showToast(`Started a new list with ${name}.`)
      else                           showToast(`${name} added to list.`)
    } catch (err) {
      console.error('[Home] add-to-list failed:', err)
      showToast('Could not add to list. Try again.')
    }
  }

  // ── Optimizer tip ─────────────────────────────────────────────────────────

  const accountAgeDays = user?.created_at
    ? Math.floor((Date.now() - new Date(user.created_at).getTime()) / (1000 * 60 * 60 * 24))
    : 0
  const totalConfirmations = rawItems?.reduce((sum, i) => sum + (i.confirmation_count || 0), 0) ?? 0

  const tip = useMemo(() => {
    if (tipDismissed) return null
    if (accountAgeDays < 7) return null
    if (totalConfirmations < 5) return null
    if (!rawItems || !householdData?.size) return null
    return findTip(rawItems, householdData.size)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawItems, householdData?.size, tipDismissed])

  function handleDismissTip() {
    localStorage.setItem('tip-dismissed', new Date().toDateString())
    setTipDismissed(true)
  }

  // ── Quick actions ─────────────────────────────────────────────────────────

  async function handleQuickAction(action: 'out' | 'used' | 'bought' | 'delete') {
    if (!quickActionItem || !householdId) return
    const itemId = quickActionItem.id

    if (action === 'out') {
      await supabase.from('pantry_items').update({ status_override: 'out' }).eq('id', itemId)
      showToast('Marked as out 📦')
    }

    if (action === 'used') {
      await supabase.from('pantry_items').update({ status_override: 'used' }).eq('id', itemId)
      showToast('Marked as used ✓')
    }

    if (action === 'bought') {
      const now = new Date().toISOString()
      await supabase.from('confirmations').insert({
        item_id:        itemId,
        household_id:   householdId,
        confirmed_at:   now,
        source:         'quick_action',
        quantity_bought: 1,
      })
      await supabase.from('pantry_items').update({
        last_confirmed_at: now,
        status_override:   null,
      }).eq('id', itemId)
      showToast('Restocked ✓')
    }

    if (action === 'delete') {
      await supabase.from('pantry_items').update({ is_active: false }).eq('id', itemId)
      showToast('Removed from pantry 🗑️')
    }

    setQuickActionItem(null)
    queryClient.invalidateQueries({ queryKey: ['pantry-items', householdId] })
  }

  function handleLongPress(item: EnrichedItem) {
    setQuickActionItem(item)
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="theme-background min-h-screen flex flex-col pb-[calc(10rem+env(safe-area-inset-bottom))]">

      {/* Toast */}
      <AnimatePresence>
        {toastData && (
          <Toast
            key="toast"
            message={toastData.message}
            onUndo={toastData.showUndo ? handleUndo : undefined}
          />
        )}
      </AnimatePresence>

      {/* Masthead */}
      <HomeMasthead
        firstName={user?.user_metadata?.display_name?.split(' ')[0] ?? null}
        state={{
          itemCount:              items.length,
          outCount,
          expiringSoonCount:      expiringItems.length,
          lastShopConfirmedAt:    undefined,
          hasMealPlannedForToday: false,
        }}
        selectMode={selectMode}
        onEnterSelect={items.length > 0 ? () => setSelectMode(true) : undefined}
        onCancelSelect={exitSelectMode}
        onSelectAll={selectAllItems}
        onClearSelect={clearSelection}
        selectedCount={totalSelected}
        allSelected={allSelected}
      />

      {/* Hero card */}
      <HomeHero
        state={{
          itemCount:              items.length,
          outCount,
          expiringSoonCount:      expiringItems.length,
          lastShopConfirmedAt:    undefined,
          hasMealPlannedForToday: false,
        }}
        onReviewList={() => navigate('/shop')}
        onPlanMeals={() => navigate('/meal-planning')}
      />

      {/* Unpack entry — banner above (conditional, only when a planned trip
          is mid-unpack) and the persistent scan button below. Lives outside
          the items-loading gate so the scan path is reachable as soon as
          the page renders. */}
      <div className="px-4">
        <PendingUnpackBanner />
        <ScanReceiptButton />
      </div>

      {/* Search bar + action tiles + expiring nudge */}
      {(!isLoading || forceShow) && (
        <div className="px-4">
          <HomeSearchBar
            value={searchQuery}
            onChange={setSearchQuery}
          />
          <HomeActionTiles
            onQuickAdd={() => navigate('/quick-add')}
            onAddItem={openAdd}
            onRecipe={() => setShowRecipeScanner(true)}
          />
          <ExpiringNudge
            count={expiringItems.length}
            onPlanMeals={() => navigate('/meal-planning')}
          />
        </div>
      )}


      {/* Loading state */}
      {isLoading && !forceShow && (
        <div className="mt-2">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)}
        </div>
      )}

      {/* Loaded state */}
      {(!isLoading || forceShow) && (
        <>
          {items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-4 px-8 text-center">
              <span className="text-5xl">🛒</span>
              <p className="text-[#2C2A26] font-semibold text-lg">Your pantry is empty</p>
              <p className="text-[#9A9491] text-sm">Add your first item to start tracking what you have at home</p>
              <button
                onClick={() => setShowSetupFlow(true)}
                className="px-6 py-3 rounded-xl text-white text-sm font-semibold"
                style={{ backgroundColor: 'var(--color-accent)' }}
              >
                🏠 Stock my pantry
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="text-sm text-[#9A9491] underline"
              >
                Add a single item instead
              </button>
            </div>
          ) : (
            <>
              {/* Audit card — shown for 1–9 items */}
              <PantryAuditCard
                itemCount={items.length}
                onStartAudit={() => setShowSetupFlow(true)}
              />

              {/* Auto-generated list banner */}
              {wasAutoGeneratedToday && !listBannerDismissed && (
                <div className="mx-4 mb-3 bg-[#E8F2EA] border border-[#6B9E78]/20 rounded-2xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">📋</span>
                      <div>
                        <p className="text-sm font-semibold text-[#2C2A26]">Your list is ready</p>
                        <p className="text-xs text-[#7A7468]">Auto-generated · also sent to your email</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => navigate('/list')}
                        className="px-3 py-1.5 rounded-xl text-xs font-semibold text-white"
                        style={{ background: '#6B9E78' }}
                      >
                        View
                      </button>
                      <button
                        onClick={() => setListBannerDismissed(true)}
                        className="text-[#9A9491] text-sm leading-none"
                        aria-label="Dismiss"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {homeLayout.map(section => {
                if (section === 'summary') return (
                  <div key="summary">
                    <div className="flex items-center justify-between pr-4">
                      <SummaryBar
                        outCount={outCount}
                        lowCount={lowCount}
                        usedCount={usedCount}
                        stockedCount={stockedCount}
                        filter={summaryBar}
                        shopUrgency={shopUrgency.urgency !== 'unknown' ? shopUrgency : undefined}
                      />
                    </div>

                    {tip && (
                      <div className="px-4 mt-3">
                        <OptimizerTip tip={tip} onDismiss={handleDismissTip} />
                      </div>
                    )}

                  </div>
                )

                if (section === 'expiring' && expiringItems.length > 0) return (
                  <div key="expiring" className="mt-4">
                    <p className="px-4 text-xs font-semibold text-[#7A7468] uppercase tracking-wide mb-2">
                      Expiring soon
                    </p>
                    <div className="flex gap-3 px-4 overflow-x-auto scrollbar-hide pb-1">
                      {expiringItems.map(item => {
                        const expDate  = new Date(item.current_expiration_date! + 'T00:00:00')
                        const todayMs  = new Date().setHours(0, 0, 0, 0)
                        const daysLeft = Math.round((expDate.getTime() - todayMs) / 86400000)
                        const expired  = daysLeft < 0
                        const label    = expired
                          ? `Expired ${Math.abs(daysLeft)}d ago`
                          : daysLeft === 0 ? 'Expires today'
                          : `${daysLeft}d left`
                        const accent   = expired ? '#C25B4E' : daysLeft <= 2 ? '#D4863A' : '#6B9E78'
                        return (
                          <button
                            key={item.id}
                            onClick={() => setConfirmExpiryItem({
                              id: item.id, name: item.name, emoji: item.emoji,
                              household_id: item.household_id,
                              current_expiration_date: item.current_expiration_date,
                            })}
                            className="shrink-0 w-28 rounded-2xl bg-white border border-[#F0EDE8]
                                       p-3 flex flex-col items-center gap-1.5 text-center
                                       hover:border-[#E8E2DC] transition-colors"
                          >
                            <span className="text-2xl">{item.emoji}</span>
                            <p className="text-xs font-semibold text-[#2C2A26] leading-tight line-clamp-2">
                              {item.name}
                            </p>
                            <p className="text-[10px] font-semibold" style={{ color: accent }}>
                              {label}
                            </p>
                            {expired && (
                              <p className="text-[10px] text-[#9A9491]">Did you use it?</p>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )

                if (section === 'items') return (
                  <div key="items">
                    {/* Restock banner */}
                    {isPremium && !dismissedRestock &&
                      (shopUrgency.urgency === 'soon' || shopUrgency.urgency === 'upcoming') &&
                      restockSummary && restockSummary.itemCount > 0 && (
                      <RestockBanner
                        summary={restockSummary}
                        shopUrgency={shopUrgency}
                        onGenerateList={() => navigate('/list')}
                        onDismiss={() => setDismissedRestock(true)}
                      />
                    )}


                    {/* Tip card */}
                    <TipCard
                      visible={tipHome.visible}
                      onDismiss={tipHome.dismiss}
                      emoji="🧠"
                      title="Pantry learns your habits"
                      steps={[
                        'Add items you regularly buy',
                        'After shopping, tap Unpack and swipe through what you got',
                        'After 3–5 restocks Pantry predicts when you\'ll run out',
                      ]}
                      cta="How it works"
                      onCta={() => navigate('/how-it-works')}
                    />



                    {/* Collapsible location sections / search results */}
                    {searchQuery.trim() ? (
                      <div className="px-4">
                        {searchResults.length === 0 ? (
                          <p className="text-sm text-[#9A9491] text-center py-8">
                            No items found for "{searchQuery}"
                          </p>
                        ) : (
                          <div className="bg-white rounded-2xl border border-[#F0EDE8] divide-y divide-[#F0EDE8] overflow-hidden">
                            {searchResults.map(item => (
                              <button
                                key={item.id}
                                onClick={() => openDetail(item)}
                                onContextMenu={e => { e.preventDefault(); handleLongPress(item) }}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#FAF8F4]"
                              >
                                <span className="text-xl shrink-0">{item.emoji}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-[#2C2A26] truncate">{item.name}</p>
                                  <p className="text-xs text-[#9A9491] capitalize">
                                    {item.display_category.replace(/_/g, ' ')} · {item.location ?? 'pantry'}
                                  </p>
                                </div>
                                <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                                  item.status === 'red'   ? 'bg-[#C25B4E]' :
                                  item.status === 'amber' ? 'bg-[#E8A838]' :
                                  item.status === 'green' ? 'bg-[#6B9E78]' : 'bg-[#C5BFB8]'
                                }`} />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="px-4">
                        {groupedByLocation.map(g => (
                          <LocationGroup
                            key={g.id}
                            locationId={g.id}
                            label={g.label}
                            items={g.items}
                            urgentCount={g.urgentCount}
                            expiringCount={g.expiringCount}
                            onItemClick={item => openDetail(item as EnrichedItem)}
                            onAdjustQty={item => setQtyAdjustItem(item)}
                            selectMode={selectMode}
                            selectedIds={selectedIds}
                            onToggleItem={toggleSelectItem}
                            onSelectAllInSection={selectAllInLocation}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )
                return null
              })}
            </>
          )}
        </>
      )}

      {/* Sticky bulk-delete bar — shown only in select mode with >= 1 item picked */}
      {selectMode && totalSelected > 0 && (
        <div className="fixed bottom-[calc(9rem+env(safe-area-inset-bottom))] left-0 right-0 z-30 px-4 max-w-[480px] mx-auto pointer-events-none">
          <div className="pointer-events-auto rounded-[14px] bg-critical shadow-lg flex items-center justify-between px-4 py-3 gap-3">
            <span className="font-serif text-[13px] italic text-cream">
              {totalSelected} item{totalSelected === 1 ? '' : 's'} selected
            </span>
            <button
              type="button"
              onClick={handleBulkDelete}
              disabled={bulkDelete.isPending}
              className="rounded-[10px] bg-cream text-critical px-4 py-2 text-[13px] font-bold disabled:opacity-50"
            >
              {bulkDelete.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      )}

      {/* ── AddItemModal ── */}
      {showAddModal && householdId && (
        <AddItemModal
          householdId={householdId}
          editItem={editItem ?? undefined}
          onClose={() => { setShowAddModal(false); setEditItem(null) }}
          onSuccess={handleItemSaved}
        />
      )}

      {/* ── ItemDetailSheet ── */}
      {detailItem && (
        <ItemDetailSheet
          item={detailItem}
          onClose={() => setDetailItem(null)}
          onEdit={handleEditFromDetail}
          onDeleted={handleDeleted}
          onMarkAsOut={handleMarkAsOutFromDetail}
        />
      )}

      {/* ── QuickQuantitySheet — tap qty pill on Home to adjust ── */}
      <QuickQuantitySheet
        isOpen={!!qtyAdjustItem}
        itemName={qtyAdjustItem?.name ?? ''}
        currentQty={qtyAdjustItem?.current_quantity ?? 0}
        unit={qtyAdjustItem?.quantity_unit ?? null}
        onClose={() => setQtyAdjustItem(null)}
        onConfirm={handleConfirmQtyAdjust}
      />

      {/* ── OutOfStockSheet — fires when qty just hit zero ── */}
      <OutOfStockSheet
        isOpen={!!oosItem}
        itemName={oosItem?.name ?? ''}
        hasActiveList={oosHasActive}
        saving={addToList.isPending}
        onClose={() => setOosItem(null)}
        onConfirm={handleOosConfirm}
      />

      {/* ── MarkAsOutSheet ── */}
      {markAsOutItem && (
        <MarkAsOutSheet
          item={markAsOutItem}
          onClose={() => setMarkAsOutItem(null)}
          onConfirm={handleMarkAsOutConfirm}
        />
      )}

      {/* ── BulkBarcodeScanner ── */}
      {showBulkScanner && householdId && (
        <BulkBarcodeScanner
          householdId={householdId}
          onClose={() => setShowBulkScanner(false)}
          onDone={count => {
            setShowBulkScanner(false)
            showToast(`${count} item${count !== 1 ? 's' : ''} added to pantry ✓`)
          }}
        />
      )}

      {/* ── PantrySetupFlow ── */}
      {showSetupFlow && householdId && (
        <PantrySetupFlow
          householdId={householdId}
          onComplete={() => {
            localStorage.setItem('pantry-setup-seen', '1')
            setShowSetupFlow(false)
          }}
          onSkip={() => {
            localStorage.setItem('pantry-setup-seen', '1')
            setShowSetupFlow(false)
          }}
        />
      )}

      {/* ── RecipeScanner ── */}
      {showRecipeScanner && householdId && (
        <RecipeScanner
          householdId={householdId}
          pantryItems={rawItems ?? []}
          onClose={() => setShowRecipeScanner(false)}
          onSuccess={showToast}
        />
      )}

      {/* ── KitchenScanner ── */}
      {showKitchenScanner && householdId && (
        <KitchenScanner
          householdId={householdId}
          onComplete={items => {
            setShowKitchenScanner(false)
            queryClient.invalidateQueries({ queryKey: ['pantry-items'] })
            showToast(`${items.length} items added ✓`)
          }}
          onClose={() => setShowKitchenScanner(false)}
        />
      )}

      {/* ── ConfirmExpirySheet ── */}
      {confirmExpiryItem && (
        <ConfirmExpirySheet
          item={confirmExpiryItem}
          onClose={() => setConfirmExpiryItem(null)}
          onSuccess={msg => { setConfirmExpiryItem(null); showToast(msg) }}
        />
      )}


      {/* ── Quick action sheet ── */}
      {quickActionItem && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
          onClick={() => setQuickActionItem(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-[460px] bg-cream rounded-t-[24px] p-5 pb-8 animate-[homeQuickSlideUp_240ms_ease-out]"
            onClick={e => e.stopPropagation()}
          >
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-border-avatar" />

            <div className="flex items-center gap-3 mb-5 px-1">
              <span className="text-[28px]">{quickActionItem.emoji}</span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-ink-muted font-medium">
                  Update
                </div>
                <h3 className="font-serif text-[20px] font-medium tracking-[-0.01em] text-ink leading-tight">
                  {quickActionItem.name}
                </h3>
              </div>
            </div>

            <div className="rounded-[16px] border border-border-sand bg-paper overflow-hidden">
              <QuickActionRow
                icon={<Package size={18} strokeWidth={1.7} />}
                tint="warn"
                title="I just ran out"
                subtitle="Adds to shopping list"
                onClick={() => handleQuickAction('out')}
              />
              <QuickActionRow
                icon={<Check size={18} strokeWidth={1.7} />}
                tint="ok"
                title="All used up"
                subtitle="Finished, not urgent"
                onClick={() => handleQuickAction('used')}
              />
              <QuickActionRow
                icon={<ShoppingBag size={18} strokeWidth={1.7} />}
                tint="forest"
                title="Just bought this"
                subtitle="Marks as restocked"
                onClick={() => handleQuickAction('bought')}
              />
              <QuickActionRow
                icon={<Trash2 size={18} strokeWidth={1.7} />}
                tint="critical"
                title="Remove from pantry"
                subtitle="Permanently delete"
                onClick={() => handleQuickAction('delete')}
                isLast
              />
            </div>

            <button
              onClick={() => setQuickActionItem(null)}
              className="w-full mt-3 rounded-[14px] border border-border-sand bg-paper py-3 text-[13px] font-semibold text-ink"
            >
              Cancel
            </button>
          </div>

          <style>{`
            @keyframes homeQuickSlideUp {
              from { transform: translateY(100%); }
              to   { transform: translateY(0); }
            }
          `}</style>
        </div>
      )}

    </div>
  )
}
